import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAzAccount } from "../../lib/auth.js";
import { getAgentVersion } from "../../lib/foundry-agent-get.js";
import { readAgentYaml } from "../../lib/agent-yaml.js";
import { parseBrainLink, isAppMode } from "@m8t-stack/api-contract";

export class BrainShowCommand extends M8tCommand {
  static paths = [["brain", "show"]];
  static usage = Command.Usage({ description: "Show a worker's brain link + connection diagnostics." });

  worker = Option.String();
  subscription = Option.String("--subscription");
  endpoint = Option.String("--endpoint");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    // Normalize clipanion descriptor objects that appear when flags are unset in tests.
    const worker = typeof this.worker === "string" ? this.worker : undefined;
    if (!worker) {
      throw new LocalCliError({ code: "USAGE", message: "<worker> positional argument is required" });
    }

    // Normalize output: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests (or not supplied), treat
    // anything that is not a plain string as "pretty" (our diagnostic default).
    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);

    const credential = new DefaultAzureCredential();

    // getAzAccount() fallback for subscriptionId (same pattern as Tasks 16-18).
    const account = await getAzAccount();
    const subscriptionId =
      (typeof this.subscription === "string" ? this.subscription : undefined) ??
      account.subscriptionId;

    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive: (this.context.stdout as NodeJS.WriteStream).isTTY === true,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
    });

    const agent = await getAgentVersion({
      credential,
      projectEndpoint: project.endpoint,
      agentName: worker,
    });

    const link = parseBrainLink(agent.metadata);
    if (!link) {
      throw new LocalCliError({
        code: "NO_LINK",
        message: `'${worker}' has no metadata.brain — not brain-enabled.`,
      });
    }

    const yaml = readAgentYaml(worker);
    const data = {
      worker,
      mode: isAppMode(link) ? "App" : "PAT",
      link,
      yamlBrain: yaml?.brain ?? null,
    };

    if (mode === "json") {
      this.context.stdout.write(renderJson(data) + "\n");
      return 0;
    }

    this.context.stdout.write(`${colors.field(worker)} (${data.mode} mode)\n`);
    this.context.stdout.write(`  repo: ${link.repo} (${link.branch})\n`);
    this.context.stdout.write(`  connection: ${link.credentialRef}\n`);
    if (link.installationId) {
      this.context.stdout.write(`  installation: ${link.installationId}\n`);
    }
    if (yaml?.brain) {
      this.context.stdout.write(`  linked at: ${yaml.brain.linkedAt}\n`);
    }
    return 0;
  }
}
